class MrIconBar extends HTMLElement {
	#value: Array<string> = [
		'start',
		'end',
	];

	#intersectionObserver = new IntersectionObserver( ( entries ) => {
		if ( isInInertTree( this ) ) {
			return;
		}

		const originalValue = new Set( this.#value );
		const value = new Set( this.#value );

		for ( const entry of entries ) {
			if ( !entry || !entry.target ) {
				continue;
			}

			if ( entry.target === this.children[0] ) {
				if ( entry.isIntersecting ) {
					value.add( 'start' );
				} else {
					value.delete( 'start' );
				}
			}

			if ( entry.target === this.children[this.children.length - 1] ) {
				if ( entry.isIntersecting ) {
					value.add( 'end' );
				} else {
					value.delete( 'end' );
				}
			}
		}

		if ( value.symmetricDifference( originalValue ).size === 0 ) {
			return;
		}

		this.#value = [
			...value.values(),
		];

		this.dispatchEvent( new Event( 'change', {
			bubbles: true,
		} ) );
	}, {
		threshold: 0.9,
		root: this,
	} );

	#mutationObserver = new MutationObserver( ( mutationList ) => {
		let hasChanges = false;

		for ( const mutation of mutationList ) {
			if ( mutation.addedNodes?.length || mutation.removedNodes?.length ) {
				hasChanges = true;
				break;
			}
		}

		if ( !hasChanges ) {
			return;
		}

		this.#intersectionObserver.disconnect();
		Array.from( this.children ).forEach( ( node ) => {
			this.#intersectionObserver.observe( node );
		} );
	} );

	get value(): string {
		return this.#value.join( ' ' );
	}

	connectedCallback(): void {
		this.#mutationObserver.observe( this, {
			attributes: false,
			childList: true,
			subtree: false,
		} );

		requestAnimationFrame( () => {
			Array.from( this.children ).forEach( ( node ) => {
				this.#intersectionObserver.observe( node );
			} );

			this.#intersectionObserver.takeRecords();
		} );
	}

	disconnectedCallback(): void {
		this.#mutationObserver.disconnect();
		this.#intersectionObserver.disconnect();
	}

	async updateState( directive: string ): Promise<void> {
		const scrollLeft = this.scrollLeft;

		switch ( directive ) {
			case 'go-to-next': {
				for ( let i = 0; i < this.children.length; i++ ) {
					const child = this.children[i];
					if ( !( 'offsetLeft' in child ) ) {
						continue;
					}

					const offsetLeft = Math.floor( ( child as HTMLElement ).offsetLeft ) - 1;

					if ( offsetLeft > scrollLeft ) {
						this.scrollTo( {
							left: offsetLeft,
							behavior: 'auto',
						} );

						break;
					}
				}

				break;
			}
			case 'go-to-previous': {
				for ( let i = ( this.children.length - 1 ); i >= 0; i-- ) {
					const child = this.children[i];
					if ( !( 'offsetLeft' in child ) ) {
						continue;
					}

					const offsetLeft = Math.ceil( ( child as HTMLElement ).offsetLeft ) + 1;

					if ( offsetLeft < scrollLeft ) {
						this.scrollTo( {
							left: offsetLeft,
							behavior: 'auto',
						} );

						break;
					}
				}

				break;
			}
			default:
				break;
		}
	}
}

customElements.define( 'mr-icon-bar', MrIconBar );

function isInInertTree( el: Element ) {
	if ( el.inert ) {
		return true;
	}

	if ( !el.parentElement ) {
		return false;
	}

	return isInInertTree( el.parentElement );
}
